import { z } from 'zod';
import type { BooleanFlag, ModuleKey } from '../enums';
import {
  amountSchema,
  currencySchema,
  idSchema,
  localDateSchema,
  markdownSchema,
  nameSchema,
  urlSchema,
} from './common';
import {
  ASSET_COMPONENT_KINDS,
  ASSET_STATUSES,
  ASSET_TYPES,
  REMINDER_AUDIENCES,
  REMINDER_CHANNELS,
  reminderOffsetsSchema,
} from './maintenance';
import { idempotencyKeySchema, mcpNoteArg, mcpQueryArg } from './mcp-base';
import { MONITOR_LIMITS, MONITOR_METHODS } from './monitoring';
import { BILLING_CYCLES, SERVICE_KINDS, SERVICE_STATUSES } from './services';

/**
 * Utrzymanie, usługi i monitoring przez MCP (DECISIONS 2026-10-09 [S] „Claude może przez konektor to samo co Sebastian
 * w pracy z klientami (pełny MCP)”): zasoby, elementy, koszty dostawców, płatności cykliczne (tylko wygenerowanie),
 * reguły przypomnień, usługi i umowy, edycja / archiwum / „Sprawdź teraz” monitora strony. Cienkie adaptery na usługi
 * modułów `maintenance`, `services`, `monitoring` (te same co tRPC aplikacji).
 *
 * Czego tu NIE ma (świadomie): sejfu (dostępy, hasła — żadne narzędzie ich nie przyjmuje ani nie zwraca), oznaczenia
 * płatności jako zafakturowanej / opłaconej (fakturę i opłacenie oznacza się w Rozliczeniach przez zestawienie i
 * Akceptacje), automatycznego przypomnienia do klienta w umowie (`auto_client_reminder` — automat wysyłki maili
 * włącza osoba w aplikacji), trybu i przełącznika symulacji monitora, monitorów serwera (porty, meldunek — adres
 * meldunku to sekret) i trwałego usuwania (tylko archiwum / przywróć).
 *
 * Konwencje jak w `mcp-daily.ts`: argumenty i wyniki snake_case, każde narzędzie mutujące ma `idempotency_key` i
 * opcjonalne `note`, edycja — `expected_version` (monitor: `expected_config_hash`, bo wersję zmienia każde
 * sprawdzenie). Kwoty tylko z „Widzi stawki i kwoty” (bez flagi pól kwot nie ma w wynikach, a zapis kwoty = FORBIDDEN).
 *
 * Integracja: integrator dopisuje `...MCP_MAINTENANCE_SERVICES_TOOLS` do `MCP_TOOLS` (contracts/mcp.ts) i usługi z
 * `@fos/core/mcp/tools/maintenance-services` (`MAINTENANCE_SERVICES_TOOL_SERVICES`) do rejestru `MCP_TOOL_SERVICES`.
 */

/** Ten sam kształt co `McpToolSpec` z contracts/mcp.ts (bez importu, żeby nie było cyklu przy rozszerzaniu rejestru). */
export interface McpMaintToolSpec {
  name: string;
  version: number;
  title: string;
  description: string;
  mutating: boolean;
  createsApproval: boolean;
  module: ModuleKey | null;
  level: 'view' | 'edit';
  flags: readonly BooleanFlag[];
  runToken: boolean;
  input: z.ZodTypeAny;
}

function tool<const N extends string>(spec: McpMaintToolSpec & { name: N }): McpMaintToolSpec & { name: N } {
  return spec;
}

const versionArg = z.number().int().positive();
const nullableText = (max: number) => z.string().trim().max(max).nullable().optional();
const atLeastOne = (c: Record<string, unknown>) => Object.values(c).some((v) => v !== undefined);
const AT_LEAST_ONE = { message: 'Podaj co najmniej jedną zmianę' };

// ── Odczyt ──────────────────────────────────────────────────────────────────

export const getAssetSetupArgs = z.object({ asset_id: idSchema }).strict();

export const listServicesArgs = z
  .object({
    client_id: idSchema.optional(),
    kind: z.enum(SERVICE_KINDS).optional(),
    status: z.enum(SERVICE_STATUSES).optional(),
    query: mcpQueryArg.optional(),
    include_archived: z.boolean().default(false),
  })
  .strict();

export const getServiceArgs = z.object({ service_id: idSchema }).strict();

// ── Zasoby ──────────────────────────────────────────────────────────────────

const assetFields = {
  type: z.enum(ASSET_TYPES),
  /** nazwa albo adres na liście, np. „helpmycar.pl”, „kontakt@rea.pl” */
  name: nameSchema,
  address: nullableText(500),
  project_id: idSchema.nullable().optional(),
  /** umowa (usługa) klienta, do której należy zasób */
  service_id: idSchema.nullable().optional(),
  /** gdzie stoi: dostawca (np. „Zenbox”) i serwer (np. „s55”) */
  provider: nullableText(100),
  server: nullableText(100),
  tech: nullableText(200),
  repo_url: urlSchema.nullable().optional(),
  status: z.enum(ASSET_STATUSES).optional(),
  notes_md: markdownSchema.nullable().optional(),
};

export const createAssetArgs = z
  .object({
    client_id: idSchema,
    ...assetFields,
    /** umowa utrzymania zakładana razem z zasobem („Utrzymanie <nazwa>”) — tylko z „Widzi stawki i kwoty” */
    contract: z
      .object({
        price: amountSchema,
        currency: currencySchema.default('PLN'),
        cycle: z.enum(BILLING_CYCLES).default('year'),
        next_payment_date: localDateSchema,
        period_start: localDateSchema.optional(),
        remind_owner: z.boolean().default(true),
        remind_client: z.boolean().default(true),
      })
      .strict()
      .optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict()
  .refine((v) => !(v.contract && v.service_id), {
    path: ['contract'],
    message: 'Zasób ma już umowę: podaj service_id albo contract',
  });

export const updateAssetArgs = z
  .object({
    asset_id: idSchema,
    /** `version` zasobu z get_asset_setup (inna = CONFLICT) */
    expected_version: versionArg,
    changes: z
      .object({ client_id: idSchema, ...assetFields })
      .partial()
      .strict()
      .refine(atLeastOne, AT_LEAST_ONE),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const archiveAssetArgs = z
  .object({
    asset_id: idSchema,
    expected_version: versionArg,
    /** false = przywróć z archiwum */
    archived: z.boolean().default(true),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Elementy zasobu ─────────────────────────────────────────────────────────

const componentFields = {
  kind: z.enum(ASSET_COMPONENT_KINDS),
  description: nullableText(500),
  provider: nullableText(100),
  expires_on: localDateSchema.nullable().optional(),
  /** w pakiecie (true) albo płatny osobno (false — koszt dodaj przez add_supplier_cost) */
  included: z.boolean().optional(),
};

export const addAssetComponentArgs = z
  .object({
    asset_id: idSchema,
    ...componentFields,
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const updateAssetComponentArgs = z
  .object({
    component_id: idSchema,
    expected_version: versionArg,
    changes: z
      .object({ ...componentFields, kind: componentFields.kind.optional() })
      .strict()
      .refine(atLeastOne, AT_LEAST_ONE),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Koszty dostawców ────────────────────────────────────────────────────────

export const addSupplierCostArgs = z
  .object({
    asset_id: idSchema,
    /** element zasobu, którego dotyczy koszt (np. domena) */
    component_id: idSchema.nullable().optional(),
    provider: z.string().trim().min(1).max(100),
    amount: amountSchema,
    currency: currencySchema.default('PLN'),
    cycle: z.enum(BILLING_CYCLES).default('year'),
    renews_on: localDateSchema,
    notes: nullableText(2000),
    /** domyślne przypomnienia do zespołu (30 i 7 dni przed) */
    reminders: z.boolean().default(true),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const updateSupplierCostArgs = z
  .object({
    supplier_cost_id: idSchema,
    expected_version: versionArg,
    changes: z
      .object({
        component_id: idSchema.nullable().optional(),
        provider: z.string().trim().min(1).max(100).optional(),
        /** tylko z „Widzi stawki i kwoty” */
        amount: amountSchema.optional(),
        currency: currencySchema.optional(),
        cycle: z.enum(BILLING_CYCLES).optional(),
        renews_on: localDateSchema.optional(),
        notes: nullableText(2000),
      })
      .strict()
      .refine(atLeastOne, AT_LEAST_ONE),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const archiveSupplierCostArgs = z
  .object({
    supplier_cost_id: idSchema,
    expected_version: versionArg,
    archived: z.boolean().default(true),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const markSupplierRenewedArgs = z
  .object({
    supplier_cost_id: idSchema,
    expected_version: versionArg,
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Płatności i przypomnienia ───────────────────────────────────────────────

export const generateRenewalPaymentArgs = z
  .object({
    /** umowa z ceną i datą następnej płatności */
    service_id: idSchema,
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

/**
 * Dni przypomnień względem terminu (przegląd Fali 12): znak niesie znaczenie, więc opis musi trafić do JSON Schema w
 * `tools/list` (JSDoc nie trafia) — inaczej „30 i 7 dni przed” zapisze się jako [7, 30], czyli po terminie.
 */
const offsetsDaysArg = reminderOffsetsSchema.describe(
  'Dni względem terminu: ujemne = PRZED terminem (np. [-30, -7] = 30 i 7 dni przed), dodatnie = PO terminie (zaległość, np. [3])',
);

export const addReminderRuleArgs = z
  .object({
    /** dokładnie jedno: umowa albo koszt dostawcy */
    service_id: idSchema.optional(),
    supplier_cost_id: idSchema.optional(),
    /** owner = zespół, client = klient (tylko umowa, kanał email_approval — mail przez Akceptacje) */
    audience: z.enum(REMINDER_AUDIENCES),
    /** dni względem terminu: ujemne = przed, dodatnie = po (np. [-30, -7]) */
    offsets_days: offsetsDaysArg,
    channel: z.enum(REMINDER_CHANNELS),
    max_repeats: z.number().int().positive().max(50).nullable().optional(),
    enabled: z.boolean().default(true),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict()
  .refine((v) => Boolean(v.service_id) !== Boolean(v.supplier_cost_id), {
    path: ['service_id'],
    message: 'Wskaż umowę (service_id) albo koszt dostawcy (supplier_cost_id)',
  });

export const updateReminderRuleArgs = z
  .object({
    rule_id: idSchema,
    expected_version: versionArg,
    changes: z
      .object({
        audience: z.enum(REMINDER_AUDIENCES).optional(),
        offsets_days: offsetsDaysArg.optional(),
        channel: z.enum(REMINDER_CHANNELS).optional(),
        max_repeats: z.number().int().positive().max(50).nullable().optional(),
        /** wyłączenie zamiast usuwania */
        enabled: z.boolean().optional(),
      })
      .strict()
      .refine(atLeastOne, AT_LEAST_ONE),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Usługi i umowy ──────────────────────────────────────────────────────────

const serviceFields = {
  name: nameSchema,
  kind: z.enum(SERVICE_KINDS),
  description_md: markdownSchema.nullable().optional(),
  period_start: localDateSchema.nullable().optional(),
  period_end: localDateSchema.nullable().optional(),
  /** warunki rozliczenia, np. „płatne z góry, faktura 7 dni” */
  billing_terms: nullableText(2000),
  /** cena netto za cykl — tylko z „Widzi stawki i kwoty” */
  price: amountSchema.nullable().optional(),
  currency: currencySchema.optional(),
  cycle: z.enum(BILLING_CYCLES).optional(),
  next_payment_date: localDateSchema.nullable().optional(),
  /** pula godzin (pakiet, utrzymanie) w sekundach */
  hours_pool_seconds: z
    .number()
    .int()
    .positive()
    .nullable()
    .optional()
    .describe('Pula godzin w SEKUNDACH (np. 10 h = 36000); null = bez puli'),
  status: z.enum(SERVICE_STATUSES).optional(),
  /** objęte zasoby (tego samego klienta); edycja = pełna lista */
  asset_ids: z.array(idSchema).max(50).optional(),
};

function periodRefinement(v: { period_start?: string | null; period_end?: string | null }, ctx: z.RefinementCtx) {
  if (v.period_start && v.period_end && v.period_end < v.period_start) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['period_end'], message: 'Koniec przed początkiem' });
  }
}

export const createServiceArgs = z
  .object({
    client_id: idSchema,
    ...serviceFields,
    /** umowa utrzymania: domyślne przypomnienia (zespół 30 i 7 dni przed, klient 30 dni przed przez Akceptacje, po terminie) */
    reminders: z
      .object({ owner: z.boolean().optional(), client: z.boolean().optional(), overdue: z.boolean().optional() })
      .strict()
      .optional(),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict()
  .superRefine(periodRefinement);

export const updateServiceArgs = z
  .object({
    service_id: idSchema,
    expected_version: versionArg,
    changes: z
      .object({ ...serviceFields, name: serviceFields.name.optional(), kind: serviceFields.kind.optional() })
      .strict()
      .refine(atLeastOne, AT_LEAST_ONE)
      .superRefine(periodRefinement),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const archiveServiceArgs = z
  .object({
    service_id: idSchema,
    expected_version: versionArg,
    archived: z.boolean().default(true),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

// ── Monitoring strony ───────────────────────────────────────────────────────

export const updateMonitorArgs = z
  .object({
    monitor_id: idSchema,
    /** `config_hash` z get_asset_setup albo poprzedniej zmiany (inna konfiguracja w bazie = CONFLICT) */
    expected_config_hash: z.string().min(1).max(128),
    changes: z
      .object({
        name: nameSchema.optional(),
        /** adres strony (http/https, publiczny) */
        url: urlSchema.optional(),
        method: z.enum(MONITOR_METHODS).optional(),
        expected_status: z
          .number()
          .int()
          .min(MONITOR_LIMITS.expectedStatus.min)
          .max(MONITOR_LIMITS.expectedStatus.max)
          .optional(),
        keyword: z.string().trim().min(1).max(MONITOR_LIMITS.keywordMax).nullable().optional(),
        interval_sec: z
          .number()
          .int()
          .min(MONITOR_LIMITS.intervalSec.min)
          .max(MONITOR_LIMITS.intervalSec.max)
          .optional(),
        timeout_ms: z.number().int().min(MONITOR_LIMITS.timeoutMs.min).max(MONITOR_LIMITS.timeoutMs.max).optional(),
        fail_threshold: z.number().int().min(MONITOR_LIMITS.threshold.min).max(MONITOR_LIMITS.threshold.max).optional(),
        recover_threshold: z
          .number()
          .int()
          .min(MONITOR_LIMITS.threshold.min)
          .max(MONITOR_LIMITS.threshold.max)
          .optional(),
        /** wstrzymaj (false) / wznów (true) sprawdzanie */
        enabled: z.boolean().optional(),
      })
      .strict()
      .refine(atLeastOne, AT_LEAST_ONE),
    note: mcpNoteArg,
    idempotency_key: idempotencyKeySchema,
  })
  .strict();

export const archiveMonitorArgs = z
  .object({ monitor_id: idSchema, note: mcpNoteArg, idempotency_key: idempotencyKeySchema })
  .strict();

export const checkMonitorNowArgs = z
  .object({ monitor_id: idSchema, note: mcpNoteArg, idempotency_key: idempotencyKeySchema })
  .strict();

// ── Rejestr narzędzi tego obszaru (wpisy w formacie `MCP_TOOLS`) ────────────

export const MCP_MAINTENANCE_SERVICES_TOOLS = [
  tool({
    name: 'get_asset_setup',
    version: 1,
    title: 'Zasób do edycji',
    description:
      'Zasób z identyfikatorami i `version` do edycji: pola zasobu, elementy, koszty dostawców, umowa, płatności, reguły przypomnień, monitor strony (`config_hash`). Użyj przed update_*/archive_*. Bez dostępów z sejfu. Kwoty tylko z „Widzi stawki i kwoty”.',
    mutating: false,
    createsApproval: false,
    module: 'maintenance',
    level: 'view',
    flags: [],
    runToken: false,
    input: getAssetSetupArgs,
  }),
  tool({
    name: 'list_services',
    version: 1,
    title: 'Usługi i umowy',
    description:
      'Usługi klientów (utrzymanie, konsultacje, pakiety godzin): nazwa, rodzaj, status, cykl, następna płatność, `version`. Cena tylko z „Widzi stawki i kwoty”. Użyj, żeby znaleźć umowę do edycji albo wygenerowania płatności.',
    mutating: false,
    createsApproval: false,
    module: null,
    level: 'view',
    flags: [],
    runToken: false,
    input: listServicesArgs,
  }),
  tool({
    name: 'get_service',
    version: 1,
    title: 'Usługa',
    description:
      'Usługa / umowa z zasobami, płatnościami cyklicznymi i regułami przypomnień (id i `version` do edycji). Cena i kwoty tylko z „Widzi stawki i kwoty”.',
    mutating: false,
    createsApproval: false,
    module: null,
    level: 'view',
    flags: [],
    runToken: false,
    input: getServiceArgs,
  }),
  tool({
    name: 'create_asset',
    version: 1,
    title: 'Dodaj zasób',
    description:
      'Dodaje zasób utrzymania (strona, aplikacja, domena, skrzynka, serwer, inne): klient, projekt, dostawca, serwer, technologia, notatki. Opcjonalnie od razu umowa z płatnością (contract — tylko z „Widzi stawki i kwoty”). Dostępów (haseł) nie przyjmuje — dodaje je osoba w sejfie.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: createAssetArgs,
  }),
  tool({
    name: 'update_asset',
    version: 1,
    title: 'Zmień zasób',
    description:
      'Zmienia pola zasobu (nazwa, adres, status, dostawca, serwer, technologia, repozytorium, projekt, umowa, notatki; przeniesienie do innego klienta wymaga edycji obu). Wymaga expected_version z get_asset_setup.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: updateAssetArgs,
  }),
  tool({
    name: 'archive_asset',
    version: 1,
    title: 'Archiwizuj zasób',
    description:
      'Przenosi zasób do archiwum (archived: false = przywraca). Nic nie jest usuwane. Wymaga expected_version.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: archiveAssetArgs,
  }),
  tool({
    name: 'add_asset_component',
    version: 1,
    title: 'Dodaj element zasobu',
    description:
      'Dodaje element zasobu (hosting, domena, SSL, baza, skrzynka, inne) z dostawcą i datą wygaśnięcia; included: false = płatny osobno (koszt: add_supplier_cost).',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: addAssetComponentArgs,
  }),
  tool({
    name: 'update_asset_component',
    version: 1,
    title: 'Zmień element zasobu',
    description:
      'Zmienia element zasobu (rodzaj, opis, dostawca, data wygaśnięcia, w pakiecie). Wymaga expected_version.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: updateAssetComponentArgs,
  }),
  tool({
    name: 'add_supplier_cost',
    version: 1,
    title: 'Dodaj koszt dostawcy',
    description:
      'Dodaje koszt u dostawcy (np. domena w OVH 60 zł/rok, odnowienie 20.10) z domyślnymi przypomnieniami dla zespołu. Tylko z „Widzi stawki i kwoty”.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: ['seesRates'],
    runToken: false,
    input: addSupplierCostArgs,
  }),
  tool({
    name: 'update_supplier_cost',
    version: 1,
    title: 'Zmień koszt dostawcy',
    description:
      'Zmienia koszt dostawcy (dostawca, cykl, data odnowienia, notatki; kwota i waluta tylko z „Widzi stawki i kwoty”). Wymaga expected_version.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: updateSupplierCostArgs,
  }),
  tool({
    name: 'archive_supplier_cost',
    version: 1,
    title: 'Archiwizuj koszt dostawcy',
    description: 'Przenosi koszt dostawcy do archiwum (archived: false = przywraca). Wymaga expected_version.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: archiveSupplierCostArgs,
  }),
  tool({
    name: 'mark_supplier_renewed',
    version: 1,
    title: 'Odnowione u dostawcy',
    description:
      'Oznacza koszt dostawcy jako odnowiony: data odnowienia przesuwa się o cykl (20.10.2026 → 20.10.2027). Użyj, gdy osoba potwierdzi, że odnowiła usługę u dostawcy. Nie dotyczy płatności klienta.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: markSupplierRenewedArgs,
  }),
  tool({
    name: 'generate_renewal_payment',
    version: 1,
    title: 'Wygeneruj płatność z umowy',
    description:
      'Tworzy bieżącą płatność cykliczną z umowy (okres od następnej płatności, kwota = cena umowy); druga próba zwraca tę samą. Fakturę i opłacenie oznacza się w Rozliczeniach (zestawienie i Akceptacje), nie tutaj. Tylko z „Widzi stawki i kwoty”.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: ['seesRates'],
    runToken: false,
    input: generateRenewalPaymentArgs,
  }),
  tool({
    name: 'add_reminder_rule',
    version: 1,
    title: 'Dodaj przypomnienie',
    description:
      'Dodaje regułę przypomnień dla umowy albo kosztu dostawcy. offsets_days: ujemne = przed terminem (np. [-30, -7]), dodatnie = po terminie. Do klienta tylko kanał email_approval — każdy mail i tak czeka w Akceptacjach.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: addReminderRuleArgs,
  }),
  tool({
    name: 'update_reminder_rule',
    version: 1,
    title: 'Zmień przypomnienie',
    description:
      'Zmienia regułę przypomnień (dni, kanał, adresat, limit powtórzeń) albo ją wyłącza (enabled: false). offsets_days: ujemne = przed terminem, dodatnie = po. Wymaga expected_version.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: updateReminderRuleArgs,
  }),
  tool({
    name: 'create_service',
    version: 1,
    title: 'Dodaj usługę',
    description:
      'Dodaje usługę klienta: umowę utrzymania (z domyślnymi przypomnieniami), konsultacje, pakiet godzin albo inną. Cena tylko z „Widzi stawki i kwoty”. Automatycznego przypomnienia do klienta nie włącza — to robi osoba w aplikacji.',
    mutating: true,
    createsApproval: false,
    module: null,
    level: 'edit',
    flags: [],
    runToken: false,
    input: createServiceArgs,
  }),
  tool({
    name: 'update_service',
    version: 1,
    title: 'Zmień usługę',
    description:
      'Zmienia usługę / umowę (nazwa, opis, okres, warunki, cykl, następna płatność, pula godzin, status, objęte zasoby; cena tylko z „Widzi stawki i kwoty”). Klienta nie zmienia. Wymaga expected_version.',
    mutating: true,
    createsApproval: false,
    module: null,
    level: 'edit',
    flags: [],
    runToken: false,
    input: updateServiceArgs,
  }),
  tool({
    name: 'archive_service',
    version: 1,
    title: 'Archiwizuj usługę',
    description:
      'Przenosi usługę do archiwum (archived: false = przywraca). Nic nie jest usuwane. Wymaga expected_version.',
    mutating: true,
    createsApproval: false,
    module: null,
    level: 'edit',
    flags: [],
    runToken: false,
    input: archiveServiceArgs,
  }),
  tool({
    name: 'update_monitor',
    version: 1,
    title: 'Zmień monitoring strony',
    description:
      'Zmienia monitor strony (adres, metoda, oczekiwany status, słowo kluczowe, częstotliwość, limity, wstrzymanie). Trybu i symulacji nie zmienia; monitorów serwera nie obsługuje. Wymaga expected_config_hash z get_asset_setup.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: updateMonitorArgs,
  }),
  tool({
    name: 'archive_monitor',
    version: 1,
    title: 'Wyłącz monitoring strony',
    description:
      'Przenosi monitor strony do archiwum (historia sprawdzeń i incydentów zostaje, otwarty incydent się zamyka). Ponowne włączenie: add_monitor.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: archiveMonitorArgs,
  }),
  tool({
    name: 'check_monitor_now',
    version: 1,
    title: 'Sprawdź teraz',
    description:
      'Zleca natychmiastowe sprawdzenie strony (najwyżej raz na 10 s; wynik po chwili w get_asset_setup albo list_incidents). Użyj po poprawce na stronie.',
    mutating: true,
    createsApproval: false,
    module: 'maintenance',
    level: 'edit',
    flags: [],
    runToken: false,
    input: checkMonitorNowArgs,
  }),
] as const;

export type McpMaintenanceServicesToolName = (typeof MCP_MAINTENANCE_SERVICES_TOOLS)[number]['name'];

const SPEC_BY_NAME = new Map<string, McpMaintToolSpec>(MCP_MAINTENANCE_SERVICES_TOOLS.map((t) => [t.name, t]));

/** Wpis narzędzia tego obszaru (przed i po dopisaniu do `MCP_TOOLS` — ten sam obiekt). */
export function getMaintenanceServicesTool(name: string): McpMaintToolSpec | undefined {
  return SPEC_BY_NAME.get(name);
}

// ── Wyniki (data w kopercie; snake_case) ────────────────────────────────────

interface Ref {
  id: string;
  name: string;
}

export interface McpMaintAsset {
  id: string;
  version: number;
  type: string;
  name: string;
  address: string | null;
  status: string;
  client: Ref;
  project: Ref | null;
  service_id: string | null;
  provider: string | null;
  server: string | null;
  tech: string | null;
  repo_url: string | null;
  notes_md: string | null;
  /** czy są dostępy w sejfie — bez etykiet, loginów i sekretów */
  has_credentials: boolean;
  archived: boolean;
  url: string;
}

export interface McpMaintComponent {
  id: string;
  version: number;
  kind: string;
  description: string | null;
  provider: string | null;
  expires_on: string | null;
  included: boolean;
}

export interface McpMaintSupplierCost {
  id: string;
  version: number;
  component_id: string | null;
  provider: string;
  /** tylko z „Widzi stawki i kwoty” */
  amount?: string;
  currency: string;
  cycle: string;
  renews_on: string;
  days_left: number;
  notes: string | null;
  archived: boolean;
}

export interface McpMaintPayment {
  id: string;
  version: number;
  period_start: string;
  period_end: string;
  /** tylko z „Widzi stawki i kwoty” */
  amount?: string;
  currency: string;
  /** pending, invoiced, paid, overdue (po terminie liczone w Europe/Warsaw) */
  status: string;
  due_date: string;
  /** w zestawieniu Rozliczeń (fakturę i opłacenie oznacza się tam) */
  in_billing_draft: boolean;
  paid_at: string | null;
}

export interface McpMaintReminderRule {
  id: string;
  version: number;
  target_type: string;
  service_id: string | null;
  supplier_cost_id: string | null;
  audience: string;
  offsets_days: number[];
  channel: string;
  max_repeats: number | null;
  enabled: boolean;
  last_fired_at: string | null;
}

export interface McpMaintService {
  id: string;
  version: number;
  client: Ref;
  name: string;
  kind: string;
  description_md: string | null;
  period_start: string | null;
  period_end: string | null;
  billing_terms: string | null;
  /** tylko z „Widzi stawki i kwoty” */
  price?: string | null;
  currency: string;
  cycle: string;
  next_payment_date: string | null;
  days_to_payment: number | null;
  hours_pool_seconds: number | null;
  status: string;
  auto_client_reminder: boolean;
  assets: { id: string; name: string; type: string }[];
  archived: boolean;
  client_url: string;
}

export interface McpMaintServiceDetail extends McpMaintService {
  /** puste bez modułu Utrzymanie albo dla usług innych niż umowa utrzymania */
  payments: McpMaintPayment[];
  reminder_rules: McpMaintReminderRule[];
}

export interface McpMaintMonitor {
  id: string;
  /** http = strona (edycja przez MCP), tcp = port, heartbeat = meldunek serwera (tylko podgląd stanu) */
  kind: string;
  name: string;
  /** strona: adres; port: host:port; meldunek: „meldunek serwera” (bez tajnego adresu) */
  target: string;
  method: string;
  expected_status: number;
  keyword: string | null;
  interval_sec: number;
  timeout_ms: number;
  fail_threshold: number;
  recover_threshold: number;
  enabled: boolean;
  mode: string;
  simulated: boolean;
  state: string;
  last_checked_at: string | null;
  next_check_at: string;
  last_error: string | null;
  /** do update_monitor (tylko monitor strony; inne rodzaje: null) */
  config_hash: string | null;
  archived: boolean;
}

export interface McpMaintAssetSetup {
  asset: McpMaintAsset;
  can_edit: boolean;
  components: McpMaintComponent[];
  supplier_costs: McpMaintSupplierCost[];
  /** umowa zasobu; null, gdy brak albo poza zakresem osoby */
  service: McpMaintService | null;
  payments: McpMaintPayment[];
  reminder_rules: McpMaintReminderRule[];
  /** monitory zasobu (bez archiwalnych): strona — edycja przez MCP; port i meldunek serwera — tylko stan */
  monitors: McpMaintMonitor[];
}
